import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  makeStyles,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import SendOutlinedIcon from '@material-ui/icons/SendOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import { Badge } from '@material-ui/core';
import clsx from 'clsx';

import { Can } from '../Can';
import {
  getAccessSnapshot,
  ACCESS_SNAPSHOT_EVENT
} from '../../context/AccessControl/accessSnapshot';
import { AuthContext } from '../../context/Auth/AuthContext';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import { i18n } from '../../translate/i18n';
import { menuGroupsConfig } from '../../utils/menuGroups';
import usePlans from '../../hooks/usePlans';
import ColorModeContext from '../../layout/themeContext';

// 0390 FT-6 — iconos de grupo para el tema `professional`.
//
// `menuGroupsConfig` trae un emoji por grupo (💬, 📊, ⚙️…). Los emojis los pinta
// el sistema operativo con su propia paleta, así que son inmunes al tema y
// contradicen el principio P2 (iconografía monocroma). Aquí se sustituyen por
// iconos que heredan el color del texto.
//
// El mapa vive en el componente y no en `menuGroupsConfig` a propósito: es
// decisión de presentación, y el manifiesto debe seguir describiendo QUÉ hay en
// el menú, no cómo se dibuja. Además, `classic` conserva sus emojis intactos.
const ICONO_GRUPO_PRO = {
  whatsapp: ForumOutlinedIcon,
  dashboard: DashboardOutlinedIcon,
  gruposWA: PeopleAltOutlinedIcon,
  mensajes: SendOutlinedIcon,
  configuracion: SettingsOutlinedIcon,
  administracion: VerifiedUserOutlinedIcon,
  flowbuilder: AccountTreeOutlinedIcon,
  utilidades: BuildOutlinedIcon,
};

/**
 * 0390 FT-6 — un solo menú, dos aspectos.
 *
 * Las MEDIDAS salen de las variables CSS que publica el tema activo
 * (`--wtx-nav-item-height`, `--wtx-nav-icon-size`…), así que `classic` y
 * `professional` comparten componente y solo cambian de datos. Esa es la razón
 * de que exista la categoría `sizing` en el catálogo: sin ella habría que
 * bifurcar este archivo por tema.
 *
 * Cada `var()` lleva su valor de reserva —el de `classic`— por si el tema no
 * llegara a cargar: el menú se dibuja igual que siempre en vez de descuadrarse.
 *
 * Lo que NO se puede expresar como medida —el desplazamiento al pasar el puntero,
 * la sombra, el fondo de tarjeta— se decide con `props.pro`. Son los rasgos que
 * `professional` elimina por principio (P4 y P7), no valores que ajustar.
 */
const useStyles = makeStyles((theme) => ({
  groupHeader: {
    borderRadius: props => props.pro ? 'var(--wtx-radius-sm, 4px)' : 'var(--wtx-radius-md, 8px)',
    margin: props => props.pro
      ? 'var(--wtx-nav-group-gap, 8px) var(--wtx-spacing-2, 8px) var(--wtx-spacing-1, 4px)'
      : '4px 8px',
    minHeight: props => props.pro ? 'var(--wtx-nav-item-height, 32px)' : undefined,
    backgroundColor: props => props.pro
      ? 'transparent'
      : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
    transition: props => props.pro
      ? 'background-color var(--wtx-duration-base, 150ms) var(--wtx-easing-standard, ease)'
      : 'all 0.3s ease-in-out',
    '& .MuiListItemIcon-root': {
      minWidth: props => props.pro ? '26px' : '40px',
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
    },
    '&:hover': {
      backgroundColor: props => props.pro
        ? 'var(--wtx-surface-hover, rgba(0,0,0,0.04))'
        : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'),
      // P7: en `professional` el movimiento se limita al color. Nada se desplaza.
      transform: props => props.pro ? 'none' : 'translateX(2px)',
    },
  },

  // ✅ ESTILOS PARA GRUPOS DE MENÚ COLAPSADO
  groupHeaderCollapsed: {
    margin: '4px 4px',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
    justifyContent: 'center',
  },

  groupTitle: {
    // P8: la jerarquía la marca la tipografía —11px en versalita— no una caja.
    fontSize: props => props.pro ? 'var(--wtx-font-size-xs, 11px)' : '16px',
    fontWeight: props => props.pro ? 'var(--wtx-font-weight-semibold, 600)' : 'bold',
    letterSpacing: props => props.pro ? '0.04em' : undefined,
    textTransform: props => props.pro ? 'uppercase' : 'none',
    color: props => props.pro ? 'var(--wtx-text-muted, #6b7280)' : theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  groupIcon: {
    fontSize: props => props.pro ? 'var(--wtx-nav-icon-size, 16px)' : '20px',
    color: props => props.pro
      ? 'var(--wtx-text-muted, #6b7280)'
      : (theme.palette.text.secondary || theme.palette.text.primary),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: 'var(--wtx-nav-icon-size, 16px)',
    },
  },

  listItem: {
    borderRadius: props => props.pro ? 'var(--wtx-radius-sm, 4px)' : 'var(--wtx-radius-md, 8px)',
    margin: props => props.pro ? '1px var(--wtx-spacing-2, 8px)' : '2px 8px',
    minHeight: props => props.pro ? 'var(--wtx-nav-item-height, 32px)' : undefined,
    paddingTop: props => props.pro ? 0 : undefined,
    paddingBottom: props => props.pro ? 0 : undefined,
    // P4: fila plana. El fondo de tarjeta es lo que hoy da aspecto recargado.
    backgroundColor: props => props.pro
      ? 'transparent !important'
      : `${theme.mode === 'dark' ? '#374151' : theme.palette.background.paper} !important`,
    color: props => props.pro
      ? 'var(--wtx-text-secondary, inherit) !important'
      : `${theme.palette.text.primary} !important`,
    transition: props => props.pro
      ? 'background-color var(--wtx-duration-base, 150ms) var(--wtx-easing-standard, ease)'
      : 'all 0.3s ease-in-out',
    '& .MuiListItemText-primary': {
      color: props => props.pro
        ? 'var(--wtx-text-secondary, inherit) !important'
        : `${theme.palette.text.primary} !important`,
      fontSize: props => props.pro ? 'var(--wtx-font-size-base, 13px)' : undefined,
    },
    '& .MuiListItemIcon-root': {
      // P2: el icono hereda el color del texto. Nunca uno propio.
      color: props => props.pro
        ? 'currentColor !important'
        : `${theme.palette.text.secondary || theme.palette.text.primary} !important`,
      minWidth: props => props.pro ? '26px' : '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: props => props.pro ? 'var(--wtx-nav-icon-size, 16px)' : '20px',
      },
    },
    '&:hover': {
      backgroundColor: props => props.pro
        ? 'var(--wtx-surface-hover, #f1f5f9) !important'
        : `${theme.mode === 'dark' ? '#4B5563' : '#E8F2FF'} !important`,
      // P7: ni desplazamiento ni sombra al pasar el puntero.
      transform: props => props.pro ? 'none' : 'translateX(4px)',
      boxShadow: props => props.pro
        ? 'none'
        : (theme.mode === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.3)'
            : '0 4px 12px rgba(59, 130, 246, 0.15)'),
      '& .MuiListItemText-primary': {
        color: props => props.pro
          ? 'var(--wtx-text-primary, inherit) !important'
          : (theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important'),
        fontWeight: props => props.pro ? 'inherit' : '500',
      },
      '& .MuiListItemIcon-root': {
        color: props => props.pro
          ? 'currentColor !important'
          : (theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important'),
      },
    },
  },

  /**
   * Ítem activo. Único punto del menú donde aparece el acento (P1), como barra
   * de 2px a la izquierda: señala sin teñir la fila entera.
   */
  listItemActive: {
    backgroundColor: 'var(--wtx-surface-selected, rgba(0,0,0,0.08)) !important',
    boxShadow: 'inset 2px 0 0 0 var(--wtx-accent, #1e3a8a)',
    '& .MuiListItemText-primary': {
      color: 'var(--wtx-text-primary, inherit) !important',
      fontWeight: 'var(--wtx-font-weight-medium, 500) !important',
    },
  },
  
  // ✅ ESTILOS PARA ELEMENTOS ANIDADOS - CORREGIDOS: Usa valores dinámicos del tema
  nestedItem: {
    paddingLeft: theme.spacing(2),
    margin: '1px 4px',
    borderRadius: '6px',
    backgroundColor: `${theme.mode === 'dark' ? '#374151' : '#f9f9f9'} !important`,
    color: `${theme.palette.text.primary} !important`,
    '& .MuiListItemText-primary': {
      color: `${theme.palette.text.primary} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.text.secondary || theme.palette.text.primary} !important`,
      minWidth: '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
    '&:hover': {
      backgroundColor: `${theme.mode === 'dark' ? '#4B5563' : '#F0F9FF'} !important`,
      transform: 'translateX(2px)',
      '& .MuiListItemText-primary': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
        fontWeight: '500',
      },
      '& .MuiListItemIcon-root': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
      },
    },
  },
  
  // ✅ ESTILOS PARA ELEMENTOS ANIDADOS COLAPSADOS
  nestedItemCollapsed: {
    margin: '2px 4px',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
    justifyContent: 'center',
    '& .MuiListItemIcon-root': {
      marginRight: 0,
      minWidth: 'auto',
    },
  },
  
  // ✅ INDICADOR DE EXPANSIÓN - CORREGIDO: Usa valores dinámicos del tema
  expandIcon: {
    color: theme.palette.text.primary,
    transition: 'transform 0.3s ease-in-out',
  },
  
  expandedIcon: {
    transform: 'rotate(180deg)',
  },
}));

// ✅ COMPONENTE DE ELEMENTO DE MENÚ (REUTILIZADO)
function ListItemLink({ icon, primary, to, className, badge, drawerOpen = true, classes, markActive, ...props }) {
  const location = useLocation();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  // 0390 FT-6 — marca del elemento activo. Solo se aplica en `professional`:
  // añadirla en `classic` cambiaría su aspecto, y ese tema debe quedar como está.
  //
  // Coincidencia por prefijo, no exacta: rutas como /tickets/123 deben seguir
  // señalando «Tickets». El guardia sobre el carácter siguiente evita que /flow
  // se dé por activo estando en /flowbuilder.
  const esActivo =
    markActive &&
    typeof to === 'string' &&
    to !== '/' &&
    (location.pathname === to ||
      (location.pathname.startsWith(to) && location.pathname[to.length] === '/'));

  return (
    <li>
      <ListItem
        button
        dense
        component={renderLink}
        className={clsx(
          className,
          esActivo && classes?.listItemActive,
          !drawerOpen && classes?.nestedItemCollapsed
        )}
        style={!drawerOpen ? {
          justifyContent: 'center',
          paddingLeft: '4px',
          paddingRight: '4px',
        } : undefined}
        {...props}
      >
        {icon ? (
          <ListItemIcon style={!drawerOpen ? { minWidth: 'auto', marginRight: 0 } : undefined}>
            {badge ? <Badge {...badge}>{icon}</Badge> : icon}
          </ListItemIcon>
        ) : null}
        {drawerOpen && <ListItemText primary={primary} />}
      </ListItem>
    </li>
  );
}

// ✅ COMPONENTE PRINCIPAL DE ACORDEÓN DE MENÚ
const AccordionMenu = ({ drawerClose, drawerOpen = true }) => {
  // 0390 FT-6 — el tema activo decide el aspecto; la estructura y los permisos
  // son los mismos para ambos.
  const { colorMode } = useContext(ColorModeContext);
  const pro = (colorMode && colorMode.navStyle) === 'flat';
  const classes = useStyles({ pro });

  /** Icono del grupo: monocromo en `professional`, emoji en `classic`. */
  const renderIconoGrupo = (group) => {
    if (!pro) return group.icon;
    const Icono = ICONO_GRUPO_PRO[group.id];
    return Icono ? <Icono fontSize="inherit" /> : null;
  };
  const history = useHistory();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ No utilizado por ahora
  
  const { user } = useContext(AuthContext);
  const { connectionWarning } = useContext(WhatsAppsContext);
  const { getPlanCompany } = usePlans();
  const isMounted = useRef(true);
  
  // ✅ ESTADO DE EXPANSIÓN DE GRUPOS
  const [expandedGroups, setExpandedGroups] = useState({
    whatsapp: true,      // ✅ Grupo principal expandido por defecto
    dashboard: false,
    gruposWA: false,
    mensajes: false,
    configuracion: false,
    utilidades: false,
  });

  // ✅ ESTADO PARA FORZAR RE-RENDERIZACIÓN CUANDO CAMBIE LA CONFIGURACIÓN DEL PLAN
  // eslint-disable-next-line no-unused-vars
  const [planConfigVersion, setPlanConfigVersion] = useState(0);

  // ✅ CARGAR CONFIGURACIÓN DEL PLAN DIRECTAMENTE
  useEffect(() => {
    async function fetchPlanConfig() {
      if (isMounted.current && user?.companyId) {
        try {
          const planConfigs = await getPlanCompany(undefined, user.companyId);
          
          if (isMounted.current && planConfigs?.plan) {
            // ✅ ACTUALIZAR LOCALSTORAGE CON LA CONFIGURACIÓN DEL PLAN
            localStorage.setItem('showCampaigns', planConfigs.plan.useCampaigns ? 'true' : 'false');
            localStorage.setItem('showKanban', planConfigs.plan.useKanban ? 'true' : 'false');
            localStorage.setItem('showOpenAi', planConfigs.plan.useOpenAi ? 'true' : 'false');
            localStorage.setItem('showIntegrations', planConfigs.plan.useIntegrations ? 'true' : 'false');
            localStorage.setItem('showSchedules', planConfigs.plan.useSchedules ? 'true' : 'false');
            localStorage.setItem('showInternalChat', planConfigs.plan.useInternalChat ? 'true' : 'false');
            localStorage.setItem('showExternalApi', planConfigs.plan.useExternalApi ? 'true' : 'false');
            localStorage.setItem('showFiles', planConfigs.plan.useFiles ? 'true' : 'false');
            
            // ✅ FORZAR RE-RENDERIZACIÓN
            setPlanConfigVersion(prev => prev + 1);
          }
        } catch (error) {
          // ✅ NO MOSTRAR ERROR SI ES 401/403 - El interceptor de API lo manejará
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // No hacer nada, el interceptor de API manejará el refresh token
          } else {
            console.error('Error cargando configuración del plan:', error);
          }
        }
      }
    }
    
    fetchPlanConfig();
    
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  // Repintar cuando cambie el snapshot de acceso (p. ej. al encender el modo
  // equipos desde Ajustes). Sin esto la entrada del menú no aparecería hasta
  // volver a entrar, que es justo la sensación de «no se guardó» que hay que
  // evitar.
  useEffect(() => {
    const alCambiarAcceso = () => setPlanConfigVersion(prev => prev + 1);
    window.addEventListener(ACCESS_SNAPSHOT_EVENT, alCambiarAcceso);
    return () => window.removeEventListener(ACCESS_SNAPSHOT_EVENT, alCambiarAcceso);
  }, []);

  // ✅ ESCUCHAR CAMBIOS EN LA CONFIGURACIÓN DEL PLAN PARA RE-RENDERIZAR
  useEffect(() => {
    // ✅ Escuchar el evento de actualización de la configuración del plan
    const handlePlanConfigUpdate = (event) => {
      setPlanConfigVersion(prev => prev + 1);
    };

    // ✅ Escuchar evento personalizado
    window.addEventListener('planConfigUpdated', handlePlanConfigUpdate);
    
    // ✅ También escuchar cambios en localStorage (para compatibilidad con otras pestañas)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('show')) {
        setPlanConfigVersion(prev => prev + 1);
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('planConfigUpdated', handlePlanConfigUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // ✅ FUNCIÓN PARA TOGGLE DE GRUPOS O NAVEGAR SI TIENE RUTA
  const handleGroupToggle = (groupId) => {
    const group = menuGroupsConfig[groupId];
    
    // ✅ Si el grupo tiene una ruta directa y no tiene items, navegar directamente
    if (group.route && (!group.items || group.items.length === 0)) {
      history.push(group.route);
      if (drawerClose) drawerClose();
      return;
    }
    
    // ✅ Si tiene items, expandir/colapsar como antes
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // ✅ FUNCIÓN PARA VERIFICAR CONDICIONALES
  const checkConditional = (conditional, item) => {
    if (!conditional) return true;
    
    let result = false;
    switch (conditional) {
      case 'showKanban':
        result = localStorage.getItem('showKanban') === 'true';
        break;
      case 'showSchedules':
        result = localStorage.getItem('showSchedules') === 'true';
        break;
      case 'showInternalChat':
        result = localStorage.getItem('showInternalChat') === 'true';
        break;
      case 'showCampaigns':
        result = localStorage.getItem('showCampaigns') === 'true';
        break;
      case 'showOpenAi':
        result = localStorage.getItem('showOpenAi') === 'true';
        break;
      case 'showIntegrations':
        result = localStorage.getItem('showIntegrations') === 'true';
        break;
      case 'showExternalApi':
        result = localStorage.getItem('showExternalApi') === 'true';
        break;
      case 'showFiles':
        result = localStorage.getItem('showFiles') === 'true';
        break;
      // La empresa trabaja con subagrupaciones (doc 0380 D7 · paso 2.A).
      //
      // No sale de `localStorage` como los del plan: es un ajuste por empresa y
      // viaja en el snapshot de acceso, que se puede recargar sin volver a
      // entrar. Así el menú reacciona en cuanto se guarda el interruptor.
      case 'teamsEnabled':
        result = getAccessSnapshot().teamsEnabled === true;
        break;
      case 'user.super':
        result =
          user &&
          (user.super === true || user.super === 1 || String(user.super) === "1");
        break;
      case 'user.superOrCompanyAdmin':
        result =
          user &&
          (user.super === true ||
            user.super === 1 ||
            String(user.super) === "1" ||
            user.profile === "admin");
        break;
      default:
        result = true;
    }
    
    return result;
  };

  // ✅ FUNCIÓN PARA CREAR BADGE SI ES NECESARIO
  const createBadge = (item) => {
    if (item.route === '/connections' && connectionWarning) {
      return { badgeContent: "!", color: "error" };
    }
    if (item.route === '/chats') {
      return { color: "secondary", variant: "dot", invisible: false };
    }
    return null;
  };

  // ✅ RENDERIZADO DE GRUPO DE MENÚ
  const renderMenuGroup = (groupId) => {
    const group = menuGroupsConfig[groupId];
    if (!group) return null;

    // ✅ VERIFICAR PERMISOS A NIVEL DE GRUPO
    // ✅ Solo el grupo "whatsapp" (CHATS) es visible para usuarios regulares
    // ✅ Todos los demás grupos (dashboard, gruposWA, mensajes, configuracion, utilidades) son solo para admins
    const isAdminGroup = groupId !== "whatsapp";
    const isElevated =
      user?.profile === "admin" ||
      user?.super === true ||
      user?.super === 1 ||
      String(user?.super) === "1";
    if (isAdminGroup && !isElevated) {
      return null;
    }

    const isExpanded = expandedGroups[groupId];
    const hasVisibleItems = group.items && group.items.length > 0 && group.items.some(item => 
      checkConditional(item.conditional, item)
    );

    // ✅ Si el grupo tiene ruta directa y no tiene items visibles, mostrar solo el header clickeable
    if (group.route && !hasVisibleItems) {
      return (
        <React.Fragment key={groupId}>
          <ListItem
            button
            component={RouterLink}
            to={group.route}
            onClick={drawerClose}
            className={clsx(classes.groupHeader, !drawerOpen && classes.groupHeaderCollapsed)}
            style={{
              paddingLeft: drawerOpen ? undefined : '4px',
              paddingRight: drawerOpen ? undefined : '4px',
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              margin: drawerOpen ? undefined : '4px 4px',
            }}
          >
            <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
              <span className={classes.groupIcon}>{renderIconoGrupo(group)}</span>
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {i18n.t(group.titleKey)}
                  </Typography>
                }
              />
            )}
          </ListItem>
          {drawerOpen && <Divider style={{ margin: '8px 0' }} />}
        </React.Fragment>
      );
    }

    if (!hasVisibleItems && !group.route) return null;

    return (
      <React.Fragment key={groupId}>
        {/* ✅ HEADER DEL GRUPO */}
        <ListItem
          button
          onClick={() => handleGroupToggle(groupId)}
          className={clsx(classes.groupHeader, !drawerOpen && classes.groupHeaderCollapsed)}
          style={{
            paddingLeft: drawerOpen ? undefined : '4px',
            paddingRight: drawerOpen ? undefined : '4px',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            margin: drawerOpen ? undefined : '4px 4px',
          }}
        >
          <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
            <span className={classes.groupIcon}>{renderIconoGrupo(group)}</span>
          </ListItemIcon>
          {drawerOpen && (
            <>
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {i18n.t(group.titleKey)}
                  </Typography>
                }
              />
              {isExpanded ? (
                <ExpandLessIcon className={`${classes.expandIcon} ${classes.expandedIcon}`} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              )}
            </>
          )}
        </ListItem>

        {/* ✅ ELEMENTOS DEL GRUPO */}
        {hasVisibleItems && (
          <>
            {drawerOpen ? (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items && group.items.map((item, index) => {
                    if (!checkConditional(item.conditional, item)) return null;

                    return (
                      <Can
                        key={`${groupId}-${index}`}
                        role={user.profile}
                        perform={item.permission}
                        yes={() => (
                          <ListItemLink
                            to={item.route}
                            primary={i18n.t(item.translationKey)}
                            icon={group.renderIcon(item.icon)}
                            className={classes.nestedItem}
                            badge={createBadge(item)}
                            onClick={drawerClose}
                            drawerOpen={drawerOpen}
                            classes={classes}
                            markActive={pro}
                          />
                        )}
                      />
                    );
                  })}
                </List>
              </Collapse>
            ) : (
              // ✅ Cuando el menú está colapsado, mostrar solo los iconos de los elementos
              <List component="div" disablePadding>
                {group.items && group.items.map((item, index) => {
                  if (!checkConditional(item.conditional, item)) return null;

                  return (
                    <Can
                      key={`${groupId}-${index}`}
                      role={user.profile}
                      perform={item.permission}
                      yes={() => (
                        <ListItemLink
                          to={item.route}
                          primary={i18n.t(item.translationKey)}
                          icon={group.renderIcon(item.icon)}
                          className={classes.nestedItem}
                          badge={createBadge(item)}
                          onClick={drawerClose}
                          drawerOpen={drawerOpen}
                          classes={classes}
                          markActive={pro}
                        />
                      )}
                    />
                  );
                })}
              </List>
            )}
          </>
        )}
        
        {/* ✅ DIVISOR ENTRE GRUPOS */}
        {drawerOpen && <Divider style={{ margin: '8px 0' }} />}
      </React.Fragment>
    );
  };

  return (
    <>
      {/* ✅ Sin wrapper onClick: un div padre con drawerClose capturaba clics y en móvil
          cerraba el drawer antes de que RouterLink completara la navegación */}
      {Object.keys(menuGroupsConfig).map(renderMenuGroup)}
    </>
  );
};

export default AccordionMenu;
